import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";

import { setAccessToken } from "../../auth";
import { isUserSessionActive } from "../../auth/isUserSessionActive";
import { clearLastActivity, deleteCookieAccessToken, deleteCookieIdToken, markActivity } from "../../auth/SecurityManager";
import { msalInstance } from "../../config/msalConfig";
import { routes } from "../../config/routes";

interface ProtectedRouteProps {
    children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
    // eslint-disable-next-line no-underscore-dangle
    const isMSEntraLogin = window.__RUNTIME_CONFIG__?.IDENTITY_SERVICE === "entra";
    const hasLoggedOut = useRef(false);
    const navigate = useNavigate();

    useEffect(() => {
        const validateSession = async () => {
            if (hasLoggedOut.current || !isMSEntraLogin) return;

            // First, check the session, after that token validity
            const tokenValid = await checkSession();
            const sessionActive = isUserSessionActive();

            if (!tokenValid || !sessionActive) {
                hasLoggedOut.current = true;
                softLogout();
            }
        };

        validateSession();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMSEntraLogin]);

    // Keep the inactivity timer honest: record real user interaction while the protected area is
    // mounted. isUserSessionActive reads this timestamp for the Entra flow.
    useEffect(() => {
        if (!isMSEntraLogin) return undefined;

        markActivity();
        const activityEvents = ["mousedown", "keydown", "scroll", "touchstart"];
        const onActivity = () => markActivity();
        activityEvents.forEach((event) => window.addEventListener(event, onActivity, { passive: true }));

        return () => activityEvents.forEach((event) => window.removeEventListener(event, onActivity));
    }, [isMSEntraLogin]);

    const checkSession = async (): Promise<boolean> => {
        try {
            const account = msalInstance.getAllAccounts()[0];
            if (!account) return false;

            const tokenResponse = await msalInstance.acquireTokenSilent({
                account,
                scopes: ["user.read"],
            });

            setAccessToken(tokenResponse.accessToken);
            return true;
        } catch {
            return false;
        }
    };

    const softLogout = async () => {
        // Clear MSAL first so getAllAccounts() is empty: this both stops the next loginRedirect from
        // silently re-authenticating an "expired" session (which fed the redirect/cookie loop) and
        // prevents baseApi.prepareHeaders from re-writing the tokens we're about to delete.
        try {
            await msalInstance.clearCache();
        } catch {
            // Best-effort — still clear the app's own state below.
        }
        deleteCookieAccessToken();
        deleteCookieIdToken();
        clearLastActivity();
        navigate(routes.login.path, { replace: true });
    };

    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
};
